import React, { useState, useEffect } from 'react';

const AutoTradeModal = ({ autoTrade, onClose, onSave, authenticatedFetch }) => {
  // 모달이 열릴 때 기존 설정 로드
  const [buyEnabled, setBuyEnabled] = useState(autoTrade?.buy_enabled || false);
  const [buyTriggerPercent, setBuyTriggerPercent] = useState(autoTrade?.buy_trigger_percent || 0);
  const [buyQuantityPercent, setBuyQuantityPercent] = useState(autoTrade?.buy_quantity_percent || 0);
  const [buyBasePrice, setBuyBasePrice] = useState(autoTrade?.buy_base_price || 0);
  const [buyUseProfitLossTrigger, setBuyUseProfitLossTrigger] = useState(autoTrade?.buy_use_profit_loss_trigger || false);
  const [buyProfitLossPercent, setBuyProfitLossPercent] = useState(autoTrade?.buy_profit_loss_percent || 0);
  const [sellEnabled, setSellEnabled] = useState(autoTrade?.sell_enabled || false);
  const [sellTriggerPercent, setSellTriggerPercent] = useState(autoTrade?.sell_trigger_percent || 0);
  const [sellQuantityPercent, setSellQuantityPercent] = useState(autoTrade?.sell_quantity_percent || 0);
  const [sellBasePrice, setSellBasePrice] = useState(autoTrade?.sell_base_price || 0);
  const [sellUseProfitLossTrigger, setSellUseProfitLossTrigger] = useState(autoTrade?.sell_use_profit_loss_trigger || false);
  const [sellProfitLossPercent, setSellProfitLossPercent] = useState(autoTrade?.sell_profit_loss_percent || 0);
  const [isActive, setIsActive] = useState(autoTrade?.is_active !== false);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  
  // 모달이 열릴 때 기존 설정 불러오기
  useEffect(() => {
    const loadExistingSettings = async () => {
      if (!autoTrade || !autoTrade.symbol || !authenticatedFetch) {
        return;
      }
      
      // 이미 설정이 있으면 사용
      if (autoTrade.buy_enabled !== undefined || autoTrade.sell_enabled !== undefined) {
        console.log('📋 기존 설정 사용:', autoTrade);
        setBuyEnabled(autoTrade.buy_enabled || false);
        setBuyTriggerPercent(autoTrade.buy_trigger_percent || 0);
        setBuyQuantityPercent(autoTrade.buy_quantity_percent || 0);
        setBuyBasePrice(autoTrade.buy_base_price || 0);
        setSellEnabled(autoTrade.sell_enabled || false);
        setSellTriggerPercent(autoTrade.sell_trigger_percent || 0);
        setSellQuantityPercent(autoTrade.sell_quantity_percent || 0);
        setSellBasePrice(autoTrade.sell_base_price || 0);
        setIsActive(autoTrade.is_active !== false);
        return;
      }
      
      // DB에서 기존 설정 불러오기 (활성화 여부와 관계없이 모든 설정 조회)
      setLoadingExisting(true);
      try {
        const response = await authenticatedFetch('/api/statistics/auto-trade?active_only=false');
        if (response.ok) {
          const data = await response.json();
          const existing = data.autoTrades?.find(at => at.symbol === autoTrade.symbol);
          if (existing) {
            console.log('✅ 기존 자동거래 설정 로드:', {
              종목: existing.symbol,
              종목명: existing.stock_name,
              활성화: existing.is_active,
              매수: existing.buy_enabled ? `${existing.buy_trigger_percent}% / ${existing.buy_quantity_percent}%` : '비활성화',
              매도: existing.sell_enabled ? `${existing.sell_trigger_percent}% / ${existing.sell_quantity_percent}%` : '비활성화'
            });
            setBuyEnabled(existing.buy_enabled || false);
            setBuyTriggerPercent(existing.buy_trigger_percent || 0);
            setBuyQuantityPercent(existing.buy_quantity_percent || 0);
            setBuyBasePrice(existing.buy_base_price || 0);
            setBuyUseProfitLossTrigger(existing.buy_use_profit_loss_trigger || false);
            setBuyProfitLossPercent(existing.buy_profit_loss_percent || 0);
            setSellEnabled(existing.sell_enabled || false);
            setSellTriggerPercent(existing.sell_trigger_percent || 0);
            setSellQuantityPercent(existing.sell_quantity_percent || 0);
            setSellBasePrice(existing.sell_base_price || 0);
            setSellUseProfitLossTrigger(existing.sell_use_profit_loss_trigger || false);
            setSellProfitLossPercent(existing.sell_profit_loss_percent || 0);
            setIsActive(existing.is_active !== false);
          } else {
            console.log('ℹ️ 기존 설정 없음, 새로 생성');
          }
        }
      } catch (error) {
        console.error('기존 설정 불러오기 실패:', error);
      } finally {
        setLoadingExisting(false);
      }
    };
    
    loadExistingSettings();
  }, [autoTrade?.symbol, authenticatedFetch]);

  const handleSave = async () => {
    // 입력 값 검증
    if (!autoTrade || !autoTrade.symbol) {
      alert('종목 정보가 없습니다.');
      return;
    }
    
    // 활성화된 경우 필수 값 검증
    if (isActive) {
      if (buyEnabled) {
        // 수익율 조건 사용 시
        if (buyUseProfitLossTrigger && buyQuantityPercent <= 0) {
          alert('매수 설정을 완료해주세요.\n(수량 % 입력 필요)');
          return;
        }
        // 기준가 조건 사용 시
        if (!buyUseProfitLossTrigger && (buyTriggerPercent <= 0 || buyQuantityPercent <= 0 || buyBasePrice <= 0)) {
          alert('매수 설정을 완료해주세요.\n(트리거 %, 수량 %, 기준가 모두 입력 필요)');
          return;
        }
      }
      if (sellEnabled) {
        // 수익율 조건 사용 시
        if (sellUseProfitLossTrigger && sellQuantityPercent <= 0) {
          alert('매도 설정을 완료해주세요.\n(수량 % 입력 필요)');
          return;
        }
        // 기준가 조건 사용 시
        if (!sellUseProfitLossTrigger && (sellTriggerPercent <= 0 || sellQuantityPercent <= 0 || sellBasePrice <= 0)) {
          alert('매도 설정을 완료해주세요.\n(트리거 %, 수량 %, 기준가 모두 입력 필요)');
          return;
        }
      }
      if (!buyEnabled && !sellEnabled) {
        alert('매수 또는 매도 중 하나 이상을 활성화해주세요.');
        return;
      }
    }
    
    setLoading(true);
    try {
      const requestData = {
        symbol: autoTrade.symbol,
        stock_name: autoTrade.stock_name || autoTrade.name || '',
        buy_enabled: buyEnabled,
        buy_trigger_percent: parseFloat(buyTriggerPercent) || 0,
        buy_quantity_percent: parseFloat(buyQuantityPercent) || 0,
        buy_base_price: parseFloat(buyBasePrice) || 0,
        buy_use_profit_loss_trigger: buyUseProfitLossTrigger,
        buy_profit_loss_percent: parseFloat(buyProfitLossPercent) || 0,
        sell_enabled: sellEnabled,
        sell_trigger_percent: parseFloat(sellTriggerPercent) || 0,
        sell_quantity_percent: parseFloat(sellQuantityPercent) || 0,
        sell_base_price: parseFloat(sellBasePrice) || 0,
        sell_use_profit_loss_trigger: sellUseProfitLossTrigger,
        sell_profit_loss_percent: parseFloat(sellProfitLossPercent) || 0,
        is_active: isActive
      };
      
      console.log('💾 자동거래 설정 저장 요청:', requestData);
      
      const response = await authenticatedFetch('/api/portfolio/auto-trade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ 자동거래 설정 저장 성공:', data);
        alert(data.message || `자동거래 설정이 저장되었습니다.\n\n종목: ${autoTrade.stock_name || autoTrade.symbol}\n설정된 조건에 따라 자동으로 거래가 실행됩니다.`);
        onSave();
      } else {
        const error = await response.json();
        console.error('❌ 자동거래 설정 저장 실패:', error);
        alert(error.error || '설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('❌ Save auto trade error:', error);
      alert('설정 저장 중 오류가 발생했습니다: ' + (error.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBasePrice = async (type) => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`/api/portfolio/auto-trade/${autoTrade.symbol}/base-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type })
      });

      if (response.ok) {
        const result = await response.json();
        if (type === 'buy' || type === 'both') {
          setBuyBasePrice(result.basePrice);
        }
        if (type === 'sell' || type === 'both') {
          setSellBasePrice(result.basePrice);
        }
        alert('기준가가 현재가로 업데이트되었습니다.');
      } else {
        const error = await response.json();
        alert(error.error || '기준가 업데이트에 실패했습니다.');
      }
    } catch (error) {
      console.error('Update base price error:', error);
      alert('기준가 업데이트 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      paddingTop: '80px', // 헤더 높이만큼 상단 여백 추가
      boxSizing: 'border-box'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: 'calc(100vh - 100px)', // 헤더 높이 고려하여 조정
        overflow: 'auto',
        marginTop: 'auto',
        marginBottom: 'auto'
      }}>
        <h2 style={{ marginTop: 0 }}>
          자동거래 설정 - {autoTrade?.stock_name || autoTrade?.name || autoTrade?.symbol || 'N/A'}
          {loadingExisting && <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>(설정 불러오는 중...)</span>}
        </h2>
        
        <div style={{ marginBottom: '20px' }}>
          <label htmlFor="auto_trade_active" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <input
              id="auto_trade_active"
              name="auto_trade_active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            자동거래 활성화
          </label>
        </div>

        {/* 매수 설정 */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
          <h3 style={{ marginTop: 0 }}>매수 설정</h3>
          <label htmlFor="buy_enabled" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <input
              id="buy_enabled"
              name="buy_enabled"
              type="checkbox"
              checked={buyEnabled}
              onChange={(e) => setBuyEnabled(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            매수 자동거래 활성화
          </label>
          {buyEnabled && (
            <>
              <div style={{ marginBottom: '10px' }}>
                <label htmlFor="buy_base_price">기준가 (원)</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    id="buy_base_price"
                    name="buy_base_price"
                    type="number"
                    value={buyBasePrice}
                    onChange={(e) => setBuyBasePrice(e.target.value)}
                    style={{ flex: 1, padding: '8px' }}
                  />
                  <button
                    onClick={() => handleUpdateBasePrice('buy')}
                    disabled={loading}
                    style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    현재가로 설정
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label htmlFor="buy_trigger_percent">매수 트리거 (%): 기준가 대비 하락률</label>
                <input
                  id="buy_trigger_percent"
                  name="buy_trigger_percent"
                  type="number"
                  value={buyTriggerPercent}
                  onChange={(e) => setBuyTriggerPercent(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  style={{ width: '100%', padding: '8px' }}
                  placeholder="예: 5"
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  💡 예약 매수 조건: 기준가 대비 몇 % 하락 시 매수할지 설정합니다.<br/>
                  예: 5% 입력 시 기준가 대비 5% 하락하면 자동으로 매수 주문이 실행됩니다.
                </small>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label htmlFor="buy_quantity_percent">매수 수량 (%): 주문가능현금 대비 비율</label>
                <input
                  id="buy_quantity_percent"
                  name="buy_quantity_percent"
                  type="number"
                  value={buyQuantityPercent}
                  onChange={(e) => setBuyQuantityPercent(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  style={{ width: '100%', padding: '8px' }}
                  placeholder="예: 10"
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  💡 예약 매수 수량 설정: 주문가능현금의 몇 %로 매수할지 설정합니다.<br/>
                  예: 10% 입력 시 주문가능현금의 10%로 매수 주문이 실행됩니다.
                </small>
              </div>
              
              {/* 수익율 조건 */}
              <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                <label htmlFor="buy_use_profit_loss_trigger" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <input
                    id="buy_use_profit_loss_trigger"
                    name="buy_use_profit_loss_trigger"
                    type="checkbox"
                    checked={buyUseProfitLossTrigger}
                    onChange={(e) => setBuyUseProfitLossTrigger(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>수익율 조건 사용 (보유 종목 평균단가 기준)</strong>
                </label>
                {buyUseProfitLossTrigger && (
                  <>
                    <div style={{ marginBottom: '10px' }}>
                      <label htmlFor="buy_profit_loss_percent">매수 수익율 조건 (%)</label>
                      <input
                        id="buy_profit_loss_percent"
                        name="buy_profit_loss_percent"
                        type="number"
                        value={buyProfitLossPercent}
                        onChange={(e) => setBuyProfitLossPercent(e.target.value)}
                        min="-100"
                        max="100"
                        step="0.1"
                        style={{ width: '100%', padding: '8px' }}
                        placeholder="예: -5 (5% 손실 시 매수)"
                      />
                      <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                        💡 수익율 조건: 보유 종목의 평균단가 기준으로 수익율이 설정값에 도달하면 매수합니다.<br/>
                        • 음수(-): 손실율 (예: -5는 5% 손실 시 추가 매수)<br/>
                        • 양수(+): 수익율 (예: +10은 10% 수익 시 추가 매수)<br/>
                        • 기준가 조건과 함께 사용 가능 (둘 중 하나 충족 시 매수)
                      </small>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* 매도 설정 */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
          <h3 style={{ marginTop: 0 }}>매도 설정</h3>
          <label htmlFor="sell_enabled" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <input
              id="sell_enabled"
              name="sell_enabled"
              type="checkbox"
              checked={sellEnabled}
              onChange={(e) => setSellEnabled(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            매도 자동거래 활성화
          </label>
          {sellEnabled && (
            <>
              <div style={{ marginBottom: '10px' }}>
                <label htmlFor="sell_base_price">기준가 (원)</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    id="sell_base_price"
                    name="sell_base_price"
                    type="number"
                    value={sellBasePrice}
                    onChange={(e) => setSellBasePrice(e.target.value)}
                    style={{ flex: 1, padding: '8px' }}
                  />
                  <button
                    onClick={() => handleUpdateBasePrice('sell')}
                    disabled={loading}
                    style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    현재가로 설정
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label htmlFor="sell_trigger_percent">매도 트리거 (%): 기준가 대비 상승률</label>
                <input
                  id="sell_trigger_percent"
                  name="sell_trigger_percent"
                  type="number"
                  value={sellTriggerPercent}
                  onChange={(e) => setSellTriggerPercent(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  style={{ width: '100%', padding: '8px' }}
                  placeholder="예: 10"
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  💡 예약 매도 조건: 기준가 대비 몇 % 상승 시 매도할지 설정합니다.<br/>
                  예: 10% 입력 시 기준가 대비 10% 상승하면 자동으로 매도 주문이 실행됩니다.
                </small>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label htmlFor="sell_quantity_percent">매도 수량 (%): 보유수량 대비 비율</label>
                <input
                  id="sell_quantity_percent"
                  name="sell_quantity_percent"
                  type="number"
                  value={sellQuantityPercent}
                  onChange={(e) => setSellQuantityPercent(e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  style={{ width: '100%', padding: '8px' }}
                  placeholder="예: 50"
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  💡 예약 매도 수량 설정: 보유수량의 몇 %를 매도할지 설정합니다.<br/>
                  예: 50% 입력 시 보유수량의 50%가 매도 주문으로 실행됩니다.
                </small>
              </div>
              
              {/* 수익율 조건 */}
              <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#d1ecf1', borderRadius: '4px', border: '1px solid #17a2b8' }}>
                <label htmlFor="sell_use_profit_loss_trigger" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <input
                    id="sell_use_profit_loss_trigger"
                    name="sell_use_profit_loss_trigger"
                    type="checkbox"
                    checked={sellUseProfitLossTrigger}
                    onChange={(e) => setSellUseProfitLossTrigger(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>수익율 조건 사용 (보유 종목 평균단가 기준)</strong>
                </label>
                {sellUseProfitLossTrigger && (
                  <>
                    <div style={{ marginBottom: '10px' }}>
                      <label htmlFor="sell_profit_loss_percent">매도 수익율 조건 (%)</label>
                      <input
                        id="sell_profit_loss_percent"
                        name="sell_profit_loss_percent"
                        type="number"
                        value={sellProfitLossPercent}
                        onChange={(e) => setSellProfitLossPercent(e.target.value)}
                        min="-100"
                        max="100"
                        step="0.1"
                        style={{ width: '100%', padding: '8px' }}
                        placeholder="예: +10 (10% 수익 시 매도)"
                      />
                      <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                        💡 수익율 조건: 보유 종목의 평균단가 기준으로 수익율이 설정값에 도달하면 매도합니다.<br/>
                        • 양수(+): 수익율 (예: +10은 10% 수익 시 매도)<br/>
                        • 음수(-): 손실율 (예: -5는 5% 손실 시 손절 매도)<br/>
                        • 기준가 조건과 함께 사용 가능 (둘 중 하나 충족 시 매도)
                      </small>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            {loading ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};


export default AutoTradeModal;
